import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useApp } from '../context/AppContext';

/**
 * Displays a compact weakness radar. Called from inside the ChatBot when the user opens Pulse.
 */
export default function WeaknessRadar({ variant = 'cyber' }) {
  const { API, user } = useApp();
  const [data, setData] = useState(null);
  const isCyber = variant === 'cyber';

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('pp-token') || '';
    axios.get(`${API}/weakness/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [user]);

  if (!user) {
    return (
      <div data-testid="weakness-radar-empty" className={`text-xs ${isCyber ? 'font-mono-cyber text-[#00FFD1]/70' : 'font-body-story italic text-[#68615A]'}`}>
        {isCyber ? '// sign in to unlock the radar' : 'Sign in to unlock the radar'}
      </div>
    );
  }
  if (!data) return null;
  if (data.total_attempts < 3) {
    return (
      <div data-testid="weakness-radar-warmup" className={`text-xs ${isCyber ? 'font-mono-cyber text-[#00FFD1]/70' : 'font-body-story italic text-[#68615A]'}`}>
        {isCyber
          ? `// need ${3 - data.total_attempts} more answers to plot radar`
          : `Answer ${3 - data.total_attempts} more questions to unlock the radar`}
      </div>
    );
  }

  const stroke = isCyber ? '#00FFD1' : '#8C1C13';
  const fill = isCyber ? 'rgba(0,255,209,0.25)' : 'rgba(140,28,19,0.2)';

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="weakness-radar"
      className="w-full">
      <div className={`text-[10px] mb-1 ${isCyber ? 'font-mono-cyber text-[#00FFD1] tracking-widest' : 'font-body-story italic text-[#68615A]'}`}>
        {isCyber ? `// weak spot: ${data.weakest_subject || '—'}` : `Weakest realm: ${data.weakest_subject || '—'}`}
      </div>
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <RadarChart data={data.radar} outerRadius="72%">
            <PolarGrid stroke={isCyber ? '#00FFD130' : '#8C6B3D40'} />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: isCyber ? '#00FFD1' : '#2C2621' }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
            <Radar dataKey="accuracy" stroke={stroke} fill={fill} fillOpacity={1} />
            <Tooltip
              contentStyle={{
                background: isCyber ? '#000' : '#FDF7E6',
                border: `1px solid ${stroke}`,
                fontSize: 11,
                color: isCyber ? '#00FFD1' : '#2C2621',
              }}
              formatter={(value, _n, p) => [`${value}% (${p.payload.correct}/${p.payload.total})`, p.payload.subject]}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}
